import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import { largeUp, mediumUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import GENERIC_CTA_CLICKED_EVENT from 'utils/Rudderstack/constants';

import Background from './Background';
import { ORIENTATION, FONT_COLORS } from './constants';
import Header from './Header';
import Headline from './Headline';
import Icon from './Icon';
import IconLandscape from './IconLandscape';

export { ORIENTATION, FONT_COLORS };

class StoryCard extends PureComponent {
  constructor(props) {
    super(props);
  }

  get backgroundImage() {
    const { orientation, backgroundImagePortrait, backgroundImageLandscape } = this.props;
    if (orientation === ORIENTATION.PORTRAIT) {
      return backgroundImagePortrait;
    }
    return backgroundImageLandscape;
  }

  get isLandscape() {
    const { orientation } = this.props;
    return orientation === ORIENTATION.LANDSCAPE;
  }

  get isPortrait() {
    const { orientation } = this.props;
    return orientation === ORIENTATION.PORTRAIT;
  }

  get shouldDisplayHeadline() {
    const { headline } = this.props;
    return headline;
  }

  get shouldDisplayHeader() {
    const { headline, icon, paragraph, header } = this.props;
    return !headline && isEmpty(icon) && (paragraph || header);
  }

  // icon cards are now legacy
  get shouldDisplayIcon() {
    const { headline, icon, paragraph } = this.props;
    return !headline && !isEmpty(icon) && paragraph;
  }

  get shouldDisplayCtaLinks() {
    const { ctaLinks } = this.props;
    return !isEmpty(ctaLinks);
  }

  render() {
    const {
      headline,
      icon,
      paragraph,
      header,
      textColor,
      backgroundColor,
      trackCtaLinkClick,
      dyCampaignData,
    } = this.props;

    return (
      <div className={'StoryCard'}>
        <Background
          isLandscape={this.isLandscape}
          isPortrait={this.isPortrait}
          backgroundColor={backgroundColor}
          image={this.backgroundImage}
        />
        <div className="StoryCard__text-component">
          {this.shouldDisplayHeadline && (
            <Headline headline={headline} textColor={textColor} isPortrait={this.isPortrait} />
          )}

          {this.shouldDisplayIcon &&
            // icon cards are now legacy
            (this.isPortrait ? (
              <Icon icon={icon} paragraph={paragraph} textColor={textColor} />
            ) : (
              <IconLandscape icon={icon} paragraph={paragraph} textColor={textColor} />
            ))}

          {this.shouldDisplayHeader && (
            <Header
              header={header}
              paragraph={paragraph}
              textColor={textColor}
              isPortrait={this.isPortrait}
            />
          )}

          {this.shouldDisplayCtaLinks && (
            <div className="StoryCard__cta-links">
              {this.props.ctaLinks.map((ctaLink, index) => (
                <a
                  key={ctaLink.label || index}
                  className="StoryCard__cta-link"
                  href={ctaLink.url}
                  target="_blank"
                  onClick={() =>
                    trackCtaLinkClick({
                      type: GENERIC_CTA_CLICKED_EVENT.TYPES.BUTTON,
                      location: GENERIC_CTA_CLICKED_EVENT.LOCATIONS.STORY_CARD,
                      name: dyCampaignData.experienceName,
                      value: dyCampaignData.experienceId,
                      url: ctaLink.url,
                      campaignName:
                        GENERIC_CTA_CLICKED_EVENT.CAMPAIGNS.COLLECTIONS_CLICKABLE_STORY_CARD,
                      variationId: dyCampaignData.variationId,
                      variationName: dyCampaignData.variationName,
                    })
                  }
                  rel="noopener noreferrer"
                >
                  {ctaLink.label}
                </a>
              ))}
            </div>
          )}
        </div>
        <style jsx>
          {`
            .StoryCard {
              width: 100%;
              height: 100%;
              min-height: 339px;
              position: relative;
            }

            @media ${mediumUp} {
              .StoryCard {
                min-height: 429px;
              }
            }
            @media ${largeUp} {
              .StoryCard {
                min-height: 216px;
              }
            }

            .StoryCard__text-component {
              position: absolute;
              width: 100%;
              height: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
              flex-direction: column;
              top: 0;
            }

            .StoryCard__cta-links {
              display: flex;
              flex-direction: column;
              align-items: center;
            }

            .StoryCard__cta-link {
              color: ${Theme.get(`colors-${textColor}`)};
              font-weight: ${Theme.get('font-weight-semi-bold')};
              text-decoration-thickness: 2px;
            }

            .StoryCard__cta-link:not(:last-child) {
              margin-bottom: ${Theme.get('spacing') * 1.5}px;
            }
          `}
        </style>
      </div>
    );
  }
}

StoryCard.propTypes = {
  paragraph: PropTypes.string,
  backgroundColor: PropTypes.string,
  headline: PropTypes.string,
  header: PropTypes.string,
  textColor: PropTypes.oneOf(Object.values(FONT_COLORS)),
  backgroundImagePortrait: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  backgroundImageLandscape: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  icon: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  orientation: PropTypes.oneOf(Object.values(ORIENTATION)),
  ctaLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  trackCtaLinkClick: PropTypes.func.isRequired,
  dyCampaignData: PropTypes.shape({
    experienceName: PropTypes.string,
    experienceId: PropTypes.number,
    variationId: PropTypes.number,
    variationName: PropTypes.string,
    campaign_name: PropTypes.string,
  }),
};

StoryCard.defaultProps = {
  paragraph: undefined,
  backgroundColor: 'black',
  headline: undefined,
  header: undefined,
  textColor: FONT_COLORS.WHITE,
  backgroundImagePortrait: {},
  backgroundImageLandscape: {},
  icon: {},
  orientation: ORIENTATION.PORTRAIT,
  ctaLinks: [],
  dyCampaignData: {},
};

export default StoryCard;
